import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowLeft, Hammer, Package, RotateCcw, Save, Scissors, Trash2 } from 'lucide-react';
import AppHeader from '../components/ui/AppHeader';
import { useCRM } from '../utils/CRMContext';
import { getProposalRates } from '../utils/appSettings';
import {
  computeHandCutVsCnc,
  HandCutInputMode,
  HandCutVsCncInputs,
} from '../utils/handCutVsCncCalc';
import './HandCutVsCncCalculator.css';

const STORAGE_KEY = 'handCutVsCncCalculator:v1';
const QUOTES_STORAGE_KEY = 'handCutVsCncSavedQuotes:v1';

export type HandCutVsCncPersisted = {
  boardFeet: number;
  handCutMode: HandCutInputMode;
  crewCount: number;
  cutDays: number;
  hoursPerDay: number;
  laborRatePerHour: number;
  handCutDollarPerBdft: number;
  cncDollarPerBdft: number;
  cncFlatFee: number;
  handOverhead: number;
  handConsumables: number;
  handDieselGallons: number;
  handDieselPricePerGal: number;
  handShipping: number;
  cncOverhead: number;
  cncConsumables: number;
  cncDieselGallons: number;
  cncDieselPricePerGal: number;
  cncShipping: number;
};

type SavedQuote = {
  id: string;
  name: string;
  savedAt: string;
  data: HandCutVsCncPersisted;
};

const defaultPersisted = (): HandCutVsCncPersisted => ({
  boardFeet: 0,
  handCutMode: 'labor',
  crewCount: 2,
  cutDays: 5,
  hoursPerDay: 8,
  laborRatePerHour: 45,
  handCutDollarPerBdft: 0,
  cncDollarPerBdft: 2,
  cncFlatFee: 0,
  handOverhead: 0,
  handConsumables: 0,
  handDieselGallons: 0,
  handDieselPricePerGal: 0,
  handShipping: 0,
  cncOverhead: 0,
  cncConsumables: 0,
  cncDieselGallons: 0,
  cncDieselPricePerGal: 0,
  cncShipping: 0,
});

function mergePersisted(raw: Partial<HandCutVsCncPersisted> | null | undefined): HandCutVsCncPersisted {
  const d = defaultPersisted();
  if (!raw || typeof raw !== 'object') return d;
  const p = raw;
  return {
    boardFeet: typeof p.boardFeet === 'number' && Number.isFinite(p.boardFeet) ? p.boardFeet : d.boardFeet,
    handCutMode: p.handCutMode === 'directBdft' ? 'directBdft' : 'labor',
    crewCount: typeof p.crewCount === 'number' ? p.crewCount : d.crewCount,
    cutDays: typeof p.cutDays === 'number' ? p.cutDays : d.cutDays,
    hoursPerDay: typeof p.hoursPerDay === 'number' ? p.hoursPerDay : d.hoursPerDay,
    laborRatePerHour: typeof p.laborRatePerHour === 'number' ? p.laborRatePerHour : d.laborRatePerHour,
    handCutDollarPerBdft:
      typeof p.handCutDollarPerBdft === 'number' ? p.handCutDollarPerBdft : d.handCutDollarPerBdft,
    cncDollarPerBdft: typeof p.cncDollarPerBdft === 'number' ? p.cncDollarPerBdft : d.cncDollarPerBdft,
    cncFlatFee: typeof p.cncFlatFee === 'number' ? p.cncFlatFee : d.cncFlatFee,
    handOverhead: typeof p.handOverhead === 'number' ? p.handOverhead : d.handOverhead,
    handConsumables: typeof p.handConsumables === 'number' ? p.handConsumables : d.handConsumables,
    handDieselGallons: typeof p.handDieselGallons === 'number' ? p.handDieselGallons : d.handDieselGallons,
    handDieselPricePerGal:
      typeof p.handDieselPricePerGal === 'number' ? p.handDieselPricePerGal : d.handDieselPricePerGal,
    handShipping: typeof p.handShipping === 'number' ? p.handShipping : d.handShipping,
    cncOverhead: typeof p.cncOverhead === 'number' ? p.cncOverhead : d.cncOverhead,
    cncConsumables: typeof p.cncConsumables === 'number' ? p.cncConsumables : d.cncConsumables,
    cncDieselGallons: typeof p.cncDieselGallons === 'number' ? p.cncDieselGallons : d.cncDieselGallons,
    cncDieselPricePerGal:
      typeof p.cncDieselPricePerGal === 'number' ? p.cncDieselPricePerGal : d.cncDieselPricePerGal,
    cncShipping: typeof p.cncShipping === 'number' ? p.cncShipping : d.cncShipping,
  };
}

function loadPersisted(): HandCutVsCncPersisted {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultPersisted();
    return mergePersisted(JSON.parse(raw) as Partial<HandCutVsCncPersisted>);
  } catch {
    return defaultPersisted();
  }
}

function loadQuotes(): SavedQuote[] {
  try {
    const raw = localStorage.getItem(QUOTES_STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return [];
    return arr
      .map((item) => {
        if (!item || typeof item !== 'object') return null;
        const o = item as Record<string, unknown>;
        const id = typeof o.id === 'string' ? o.id : '';
        const name = typeof o.name === 'string' ? o.name : '';
        const savedAt = typeof o.savedAt === 'string' ? o.savedAt : '';
        const data = mergePersisted(o.data as Partial<HandCutVsCncPersisted>);
        if (!id || !name) return null;
        return { id, name, savedAt: savedAt || new Date().toISOString(), data };
      })
      .filter((q): q is SavedQuote => q !== null);
  } catch {
    return [];
  }
}

function writeQuotes(quotes: SavedQuote[]) {
  try {
    localStorage.setItem(QUOTES_STORAGE_KEY, JSON.stringify(quotes));
  } catch {
    /* ignore */
  }
}

const HandCutVsCncCalculator: React.FC = () => {
  const [searchParams] = useSearchParams();
  const linkedProjectId = searchParams.get('projectId')?.trim() || '';
  const { timberProjects, updateTimberProject } = useCRM();
  const linkedProject = useMemo(
    () => (linkedProjectId ? timberProjects.find((tp) => tp.id === linkedProjectId) : null),
    [linkedProjectId, timberProjects],
  );

  const [ratesLoaded, setRatesLoaded] = useState(false);
  const [p, setP] = useState<HandCutVsCncPersisted>(() => loadPersisted());
  const [quotes, setQuotes] = useState<SavedQuote[]>(() => loadQuotes());
  const [quoteNameInput, setQuoteNameInput] = useState('');
  const [selectedQuoteId, setSelectedQuoteId] = useState<string>('');
  const [quoteMessage, setQuoteMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const hadStored = !!localStorage.getItem(STORAGE_KEY);
      try {
        const rates = await getProposalRates();
        if (cancelled) return;
        if (!hadStored && rates.productionRatePerBdft > 0) {
          setP((prev) => ({
            ...prev,
            cncDollarPerBdft: rates.productionRatePerBdft,
          }));
        }
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setRatesLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!linkedProject?.handCutVsCncState) return;
    const s = linkedProject.handCutVsCncState;
    setP((prev) =>
      mergePersisted({
        boardFeet: s.boardFeet,
        handCutMode: s.handCutMode,
        crewCount: s.crewCount,
        cutDays: s.cutDays,
        hoursPerDay: s.hoursPerDay,
        laborRatePerHour: s.laborRatePerHour,
        handCutDollarPerBdft: s.handCutDollarPerBdft,
        cncDollarPerBdft: s.cncDollarPerBdft,
        cncFlatFee: s.cncFlatFee,
        handOverhead: s.handOverhead,
        handConsumables: s.handConsumables,
        handDieselGallons: s.handDieselGallons,
        handDieselPricePerGal: s.handDieselPricePerGal,
        handShipping: s.handShipping,
        cncOverhead: s.cncOverhead,
        cncConsumables: s.cncConsumables,
        cncDieselGallons: s.cncDieselGallons,
        cncDieselPricePerGal: s.cncDieselPricePerGal,
        cncShipping: s.cncShipping,
      }),
    );
  }, [linkedProject?.id, linkedProject?.handCutVsCncState]);

  const persist = useCallback(
    (next: HandCutVsCncPersisted) => {
      setP(next);
      if (!linkedProjectId) {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        } catch {
          /* ignore */
        }
        return;
      }
      void updateTimberProject(linkedProjectId, {
        handCutVsCncState: { ...next, updatedAt: new Date() },
        fabricationMethod: 'comparing',
      });
    },
    [linkedProjectId, updateTimberProject],
  );

  const refreshQuotes = useCallback(() => {
    setQuotes(loadQuotes());
  }, []);

  const inputs: HandCutVsCncInputs = useMemo(
    () => ({
      boardFeet: p.boardFeet,
      handCutMode: p.handCutMode,
      crewCount: p.crewCount,
      cutDays: p.cutDays,
      hoursPerDay: p.hoursPerDay,
      laborRatePerHour: p.laborRatePerHour,
      handCutDollarPerBdft: p.handCutDollarPerBdft,
      cncDollarPerBdft: p.cncDollarPerBdft,
      cncFlatFee: p.cncFlatFee,
      handOverhead: p.handOverhead,
      handConsumables: p.handConsumables,
      handDieselGallons: p.handDieselGallons,
      handDieselPricePerGal: p.handDieselPricePerGal,
      handShipping: p.handShipping,
      cncOverhead: p.cncOverhead,
      cncConsumables: p.cncConsumables,
      cncDieselGallons: p.cncDieselGallons,
      cncDieselPricePerGal: p.cncDieselPricePerGal,
      cncShipping: p.cncShipping,
    }),
    [p],
  );

  const result = useMemo(() => computeHandCutVsCnc(inputs), [inputs]);

  const chartData = useMemo(
    () => [
      { name: 'Hand cut', value: result.handCutTotal, key: 'hand' as const },
      { name: 'CNC supplier', value: result.cncTotal, key: 'cnc' as const },
    ],
    [result.handCutTotal, result.cncTotal],
  );

  const formatUsd = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

  const summaryLine = useMemo(() => {
    const hasScope = p.boardFeet > 0;
    const hasMoney = result.handCutTotal > 0.005 || result.cncTotal > 0.005;
    if (!hasScope && !hasMoney) {
      return 'Enter costs or board feet to compare hand cut vs CNC.';
    }
    if (result.cheaper === 'tie') {
      return 'Roughly the same total at this scope.';
    }
    if (result.savingsPercentVsHand > 0) {
      if (result.cheaper === 'cnc') {
        return `CNC is about ${result.savingsPercentVsHand}% less than hand cut (vs hand total).`;
      }
      return `Hand cut is about ${result.savingsPercentVsHand}% less than CNC (vs CNC total).`;
    }
    const abs = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(
      Math.abs(result.deltaHandMinusCnc),
    );
    if (result.cheaper === 'cnc') {
      return `CNC saves ${abs} vs hand cut at this scope.`;
    }
    return `Hand cut saves ${abs} vs CNC at this scope.`;
  }, [p.boardFeet, result.cheaper, result.deltaHandMinusCnc, result.handCutTotal, result.cncTotal, result.savingsPercentVsHand]);

  const resetDefaults = () => {
    persist(defaultPersisted());
    setQuoteMessage(null);
  };

  const update = (patch: Partial<HandCutVsCncPersisted>) => {
    persist({ ...p, ...patch });
  };

  const saveNamedQuote = () => {
    const name = quoteNameInput.trim();
    if (!name) {
      setQuoteMessage('Enter a name for this quote.');
      return;
    }
    const id =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `q_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const next: SavedQuote = { id, name, savedAt: new Date().toISOString(), data: { ...p } };
    const list = [...loadQuotes(), next].sort((a, b) => a.name.localeCompare(b.name));
    writeQuotes(list);
    refreshQuotes();
    setSelectedQuoteId(id);
    setQuoteNameInput('');
    setQuoteMessage(`Saved “${name}”.`);
  };

  const loadSelectedQuote = () => {
    if (!selectedQuoteId) {
      setQuoteMessage('Choose a saved quote to load.');
      return;
    }
    const q = loadQuotes().find((x) => x.id === selectedQuoteId);
    if (!q) {
      setQuoteMessage('That quote was not found.');
      refreshQuotes();
      return;
    }
    persist(mergePersisted(q.data));
    setQuoteMessage(`Loaded “${q.name}”.`);
  };

  const deleteSelectedQuote = () => {
    if (!selectedQuoteId) {
      setQuoteMessage('Choose a saved quote to delete.');
      return;
    }
    const q = loadQuotes().find((x) => x.id === selectedQuoteId);
    const list = loadQuotes().filter((x) => x.id !== selectedQuoteId);
    writeQuotes(list);
    refreshQuotes();
    setSelectedQuoteId('');
    setQuoteMessage(q ? `Deleted “${q.name}”.` : 'Deleted.');
  };

  const barColor = (key: 'hand' | 'cnc') => {
    if (result.cheaper === 'tie') return key === 'hand' ? '#5c7a63' : '#4a6fa5';
    if (result.cheaper === 'cnc') return key === 'cnc' ? '#2f7a3a' : '#8a8e8b';
    return key === 'hand' ? '#2f7a3a' : '#8a8e8b';
  };

  const showChart = result.handCutTotal > 0.005 || result.cncTotal > 0.005;

  return (
    <div className="hcvc-page">
      <AppHeader />
      <div className="hcvc-inner">
        <header className="hcvc-header">
          <div className="hcvc-header-title">
            <Link to="/tools" className="hcvc-back">
              <ArrowLeft size={18} /> Tools
            </Link>
            <h1>
              <Scissors size={22} strokeWidth={2} aria-hidden />
              Hand cut vs CNC
            </h1>
            <p className="hcvc-sub">
              Compare shop labor, overhead, consumables, diesel, and shipping to a CNC supplier quote on the same
              scope.
              {linkedProject && (
                <>
                  {' '}
                  Linked to project: <strong>{linkedProject.name}</strong> (saved to Project Hub).
                </>
              )}
            </p>
          </div>
          <div className="hcvc-header-actions">
            <button type="button" className="hcvc-btn hcvc-btn--ghost" onClick={resetDefaults}>
              <RotateCcw size={16} /> Reset
            </button>
          </div>
        </header>

        <section className="hcvc-card hcvc-quotes-bar" aria-label="Saved quotes">
          <div className="hcvc-quotes-row">
            <div className="hcvc-quotes-icon">
              <Package size={20} aria-hidden />
            </div>
            <div className="hcvc-quotes-fields">
              <label className="hcvc-label hcvc-label--inline">
                <span className="hcvc-label-text">Save current as</span>
                <input
                  type="text"
                  className="hcvc-input"
                  value={quoteNameInput}
                  onChange={(e) => setQuoteNameInput(e.target.value)}
                  placeholder="Quote name (e.g. Smith barn — rev 2)"
                  maxLength={120}
                />
              </label>
              <button type="button" className="hcvc-btn hcvc-btn--primary" onClick={saveNamedQuote}>
                <Save size={16} /> Save quote
              </button>
            </div>
          </div>
          <div className="hcvc-quotes-row hcvc-quotes-row--second">
            <label className="hcvc-label hcvc-label--grow">
              <span className="hcvc-label-text">Saved quotes</span>
              <select
                className="hcvc-input hcvc-select"
                value={selectedQuoteId}
                onChange={(e) => setSelectedQuoteId(e.target.value)}
              >
                <option value="">— Select —</option>
                {quotes.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.name}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="hcvc-btn" onClick={loadSelectedQuote}>
              Load
            </button>
            <button type="button" className="hcvc-btn hcvc-btn--danger" onClick={deleteSelectedQuote}>
              <Trash2 size={16} /> Delete
            </button>
          </div>
          {quoteMessage && <p className="hcvc-quote-msg">{quoteMessage}</p>}
          <p className="hcvc-hint hcvc-hint--quotes">Quotes are stored in this browser only (localStorage).</p>
        </section>

        <div className="hcvc-layout">
          <div className="hcvc-main">
            <section className="hcvc-card">
              <h2 className="hcvc-card-title">Scope</h2>
              <label className="hcvc-label">
                Total board feet
                <input
                  type="number"
                  min={0}
                  step={1}
                  className="hcvc-input"
                  value={p.boardFeet || ''}
                  onChange={(e) => update({ boardFeet: Math.max(0, parseFloat(e.target.value) || 0) })}
                  placeholder="0"
                />
              </label>
            </section>

            <section className="hcvc-card">
              <h2 className="hcvc-card-title">
                <Hammer size={18} aria-hidden /> Hand cut
              </h2>
              <div className="hcvc-mode-row">
                <label className="hcvc-radio">
                  <input
                    type="radio"
                    name="handCutMode"
                    checked={p.handCutMode === 'labor'}
                    onChange={() => update({ handCutMode: 'labor' })}
                  />
                  Labor estimate
                </label>
                <label className="hcvc-radio">
                  <input
                    type="radio"
                    name="handCutMode"
                    checked={p.handCutMode === 'directBdft'}
                    onChange={() => update({ handCutMode: 'directBdft' })}
                  />
                  Direct $/bdft
                </label>
              </div>

              {p.handCutMode === 'labor' ? (
                <div className="hcvc-grid">
                  <label className="hcvc-label">
                    Crew (count)
                    <input
                      type="number"
                      min={0}
                      step={1}
                      className="hcvc-input"
                      value={p.crewCount}
                      onChange={(e) => update({ crewCount: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                    />
                  </label>
                  <label className="hcvc-label">
                    Days
                    <input
                      type="number"
                      min={0}
                      step={0.25}
                      className="hcvc-input"
                      value={p.cutDays}
                      onChange={(e) => update({ cutDays: Math.max(0, parseFloat(e.target.value) || 0) })}
                    />
                  </label>
                  <label className="hcvc-label">
                    Hours / day
                    <input
                      type="number"
                      min={0}
                      step={0.5}
                      className="hcvc-input"
                      value={p.hoursPerDay}
                      onChange={(e) => update({ hoursPerDay: Math.max(0, parseFloat(e.target.value) || 0) })}
                    />
                  </label>
                  <label className="hcvc-label">
                    Blended $/hr
                    <input
                      type="number"
                      min={0}
                      step={1}
                      className="hcvc-input"
                      value={p.laborRatePerHour}
                      onChange={(e) => update({ laborRatePerHour: Math.max(0, parseFloat(e.target.value) || 0) })}
                    />
                  </label>
                </div>
              ) : (
                <label className="hcvc-label">
                  Hand cut $/bdft
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    className="hcvc-input"
                    value={p.handCutDollarPerBdft}
                    onChange={(e) =>
                      update({ handCutDollarPerBdft: Math.max(0, parseFloat(e.target.value) || 0) })
                    }
                  />
                </label>
              )}

              <h3 className="hcvc-subsection-title">Overhead, consumables, diesel, shipping</h3>
              <div className="hcvc-grid">
                <label className="hcvc-label">
                  Overhead ($)
                  <input
                    type="number"
                    min={0}
                    step={1}
                    className="hcvc-input"
                    value={p.handOverhead}
                    onChange={(e) => update({ handOverhead: Math.max(0, parseFloat(e.target.value) || 0) })}
                  />
                </label>
                <label className="hcvc-label">
                  Consumables ($)
                  <input
                    type="number"
                    min={0}
                    step={1}
                    className="hcvc-input"
                    value={p.handConsumables}
                    onChange={(e) => update({ handConsumables: Math.max(0, parseFloat(e.target.value) || 0) })}
                  />
                </label>
                <label className="hcvc-label">
                  Diesel (gal)
                  <input
                    type="number"
                    min={0}
                    step={0.1}
                    className="hcvc-input"
                    value={p.handDieselGallons}
                    onChange={(e) => update({ handDieselGallons: Math.max(0, parseFloat(e.target.value) || 0) })}
                  />
                </label>
                <label className="hcvc-label">
                  Diesel ($/gal)
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    className="hcvc-input"
                    value={p.handDieselPricePerGal}
                    onChange={(e) => update({ handDieselPricePerGal: Math.max(0, parseFloat(e.target.value) || 0) })}
                  />
                </label>
                <label className="hcvc-label hcvc-grid-span2">
                  Shipping ($)
                  <input
                    type="number"
                    min={0}
                    step={1}
                    className="hcvc-input"
                    value={p.handShipping}
                    onChange={(e) => update({ handShipping: Math.max(0, parseFloat(e.target.value) || 0) })}
                  />
                </label>
              </div>

              {p.handCutMode === 'labor' && p.boardFeet > 0 && (
                <p className="hcvc-hint">
                  Implied {formatUsd(result.handCutImpliedDollarPerBdft)} / bdft on full hand-cut total (
                  {result.handCutLaborHours} shop hours).
                </p>
              )}
            </section>

            <section className="hcvc-card">
              <h2 className="hcvc-card-title">CNC supplier</h2>
              <div className="hcvc-grid">
                <label className="hcvc-label">
                  $/bdft (quote)
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    className="hcvc-input"
                    value={p.cncDollarPerBdft}
                    onChange={(e) => update({ cncDollarPerBdft: Math.max(0, parseFloat(e.target.value) || 0) })}
                  />
                </label>
                <label className="hcvc-label">
                  Flat / setup ($)
                  <input
                    type="number"
                    min={0}
                    step={1}
                    className="hcvc-input"
                    value={p.cncFlatFee}
                    onChange={(e) => update({ cncFlatFee: Math.max(0, parseFloat(e.target.value) || 0) })}
                  />
                </label>
              </div>
              {!ratesLoaded ? (
                <p className="hcvc-hint">Loading default rate from proposal settings…</p>
              ) : (
                <p className="hcvc-hint">Variable CNC portion: {formatUsd(result.cncVariablePortion)} (bdft × rate).</p>
              )}

              <h3 className="hcvc-subsection-title">Overhead, consumables, diesel, shipping</h3>
              <div className="hcvc-grid">
                <label className="hcvc-label">
                  Overhead ($)
                  <input
                    type="number"
                    min={0}
                    step={1}
                    className="hcvc-input"
                    value={p.cncOverhead}
                    onChange={(e) => update({ cncOverhead: Math.max(0, parseFloat(e.target.value) || 0) })}
                  />
                </label>
                <label className="hcvc-label">
                  Consumables ($)
                  <input
                    type="number"
                    min={0}
                    step={1}
                    className="hcvc-input"
                    value={p.cncConsumables}
                    onChange={(e) => update({ cncConsumables: Math.max(0, parseFloat(e.target.value) || 0) })}
                  />
                </label>
                <label className="hcvc-label">
                  Diesel (gal)
                  <input
                    type="number"
                    min={0}
                    step={0.1}
                    className="hcvc-input"
                    value={p.cncDieselGallons}
                    onChange={(e) => update({ cncDieselGallons: Math.max(0, parseFloat(e.target.value) || 0) })}
                  />
                </label>
                <label className="hcvc-label">
                  Diesel ($/gal)
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    className="hcvc-input"
                    value={p.cncDieselPricePerGal}
                    onChange={(e) => update({ cncDieselPricePerGal: Math.max(0, parseFloat(e.target.value) || 0) })}
                  />
                </label>
                <label className="hcvc-label hcvc-grid-span2">
                  Shipping ($)
                  <input
                    type="number"
                    min={0}
                    step={1}
                    className="hcvc-input"
                    value={p.cncShipping}
                    onChange={(e) => update({ cncShipping: Math.max(0, parseFloat(e.target.value) || 0) })}
                  />
                </label>
              </div>
            </section>
          </div>

          <aside className="hcvc-side">
            <section className="hcvc-card hcvc-summary">
              <h2 className="hcvc-card-title">Comparison</h2>

              <div className="hcvc-breakdown">
                <div className="hcvc-breakdown-head">Hand cut</div>
                <div className="hcvc-breakdown-row">
                  <span>Core (labor or bdft × rate)</span>
                  <span>{formatUsd(result.handCutCore)}</span>
                </div>
                <div className="hcvc-breakdown-row">
                  <span>Overhead</span>
                  <span>{formatUsd(result.handOverhead)}</span>
                </div>
                <div className="hcvc-breakdown-row">
                  <span>Consumables</span>
                  <span>{formatUsd(result.handConsumables)}</span>
                </div>
                <div className="hcvc-breakdown-row">
                  <span>Diesel</span>
                  <span>{formatUsd(result.handDieselCost)}</span>
                </div>
                <div className="hcvc-breakdown-row">
                  <span>Shipping</span>
                  <span>{formatUsd(result.handShipping)}</span>
                </div>
                <div className="hcvc-breakdown-row hcvc-breakdown-strong">
                  <span>Total</span>
                  <span>{formatUsd(result.handCutTotal)}</span>
                </div>
              </div>

              <div className="hcvc-breakdown hcvc-breakdown--cnc">
                <div className="hcvc-breakdown-head">CNC supplier</div>
                <div className="hcvc-breakdown-row">
                  <span>Core (flat + bdft × rate)</span>
                  <span>{formatUsd(result.cncCore)}</span>
                </div>
                <div className="hcvc-breakdown-row">
                  <span>Overhead</span>
                  <span>{formatUsd(result.cncOverhead)}</span>
                </div>
                <div className="hcvc-breakdown-row">
                  <span>Consumables</span>
                  <span>{formatUsd(result.cncConsumables)}</span>
                </div>
                <div className="hcvc-breakdown-row">
                  <span>Diesel</span>
                  <span>{formatUsd(result.cncDieselCost)}</span>
                </div>
                <div className="hcvc-breakdown-row">
                  <span>Shipping</span>
                  <span>{formatUsd(result.cncShipping)}</span>
                </div>
                <div className="hcvc-breakdown-row hcvc-breakdown-strong">
                  <span>Total</span>
                  <span>{formatUsd(result.cncTotal)}</span>
                </div>
              </div>

              <div className="hcvc-totals hcvc-totals--delta">
                <div className="hcvc-delta">
                  <span className="hcvc-total-label">Delta (hand − CNC)</span>
                  <span
                    className={`hcvc-total-value ${
                      result.deltaHandMinusCnc > 0 ? 'hcvc-more' : result.deltaHandMinusCnc < 0 ? 'hcvc-less' : ''
                    }`}
                  >
                    {formatUsd(result.deltaHandMinusCnc)}
                  </span>
                </div>
              </div>
              <p className="hcvc-summary-text">{summaryLine}</p>

              {showChart && (
                <div className="hcvc-chart-wrap">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e3e1da" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tickFormatter={(v) => `$${v}`} tick={{ fontSize: 11 }} width={56} />
                      <Tooltip
                        formatter={(value) =>
                          formatUsd(typeof value === 'number' ? value : Number(value) || 0)
                        }
                      />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                        {chartData.map((entry) => (
                          <Cell key={entry.key} fill={barColor(entry.key)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default HandCutVsCncCalculator;
